import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Container,
  Table,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  InputGroup,
  InputGroupText,
  UncontrolledTooltip,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Pagination,
  PaginationItem,
  PaginationLink,
  Progress,
  Alert,
  Collapse
} from 'reactstrap'; 

// Importar cliente de Supabase desde el archivo supabaseClient
import { supabase } from '../services/supabaseClient'; // Ajusta la ruta según tu estructura

// Cache global para almacenar los datos
let globalPACCache = {
  data: null,
  totalRecords: 0,
  timestamp: null,
  isLoading: false
};

const CACHE_DURATION = 60 * 60 * 1000; // 60 minutos en milisegundos
const BATCH_SIZE = 1000; // Tamaño del lote para cargar datos

const PACGeneral = ({ user }) => {
  // Estados principales
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // Estados para el sistema de revisión
  const [processosRevisados, setProcessosRevisados] = useState(new Set());
  const [loadingReviewed, setLoadingReviewed] = useState(false);
  
  // Estados para filtros
  const [departamentosSeleccionados, setDepartamentosSeleccionados] = useState([]);
  const [filtroDescripcion, setFiltroDescripcion] = useState('');
  const [filtroTipoObjeto, setFiltroTipoObjeto] = useState('');
  const [filtroNomenclatura, setFiltroNomenclatura] = useState('');
  const [filtroEntidad, setFiltroEntidad] = useState('');
  const [filtroEstadoRevision, setFiltroEstadoRevision] = useState('todos');
  
  // Estados para dropdowns
  const [dropdownDepartamento, setDropdownDepartamento] = useState(false);
  const [searchDepartamento, setSearchDepartamento] = useState('');
  
  // Estados para ordenamiento y modal
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedContract, setSelectedContract] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Estados para tooltips de descripción
  const [hoveredDescription, setHoveredDescription] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Estados para responsividad
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Referencias para dropdowns
  const dropdownDepartamentoRef = useRef(null);
  const hasInitialized = useRef(false);

const getUserIdentifier = (user) => {
  if (!user) return null;
  
  // ORDEN CONSISTENTE: email > user_metadata.email > id
  const identifier = user.email || user.user_metadata?.email || user.id;
  
  console.log('PAC getUserIdentifier:', {
    email: user.email,
    metadataEmail: user.user_metadata?.email,
    id: user.id,
    selected: identifier
  });
  
  return identifier;
};

  // Detectar dispositivos móviles y tablets
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
      
      // En móvil, mostrar menos elementos por página por defecto
      if (width <= 768 && itemsPerPage > 10) {
        setItemsPerPage(10);
      }
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, [itemsPerPage]);

// REEMPLAZAR loadUserReviewedProcesses:
const loadUserReviewedProcesses = async (user) => {
  if (!user) {
    console.log('No hay usuario para cargar procesos revisados');
    return;
  }
  
  const userIdentifier = getUserIdentifier(user);
  
  if (!userIdentifier) {
    console.log('No se pudo obtener identificador del usuario:', user);
    return;
  }
  
  try {
    setLoadingReviewed(true);
    console.log('Cargando procesos revisados para usuario:', userIdentifier);
    
    const { data, error } = await supabase
      .from('user_reviewed_processes')
      .select('process_id')
      .eq('user_id', userIdentifier)
      .eq('fuente', 'PAC');

    if (error) {
      console.error('Error cargando procesos revisados:', error);
      throw error;
    }

    const reviewedIds = new Set(data.map(item => item.process_id));
    setProcessosRevisados(reviewedIds);
    console.log('Procesos PAC revisados cargados:', reviewedIds.size);
    
  } catch (error) {
    console.error('Error cargando procesos revisados:', error);
  } finally {
    setLoadingReviewed(false);
  }
};

// REEMPLAZAR toggleProcessReviewed:
const toggleProcessReviewed = async (process, user) => {
  console.log('=== TOGGLE PROCESS PAC ===');
  console.log('Process:', process);
  console.log('User completo:', user);
  
  const userIdentifier = getUserIdentifier(user);
  
  if (!userIdentifier) {
    console.error('No se pudo identificar al usuario');
    console.error('User object:', user);
    alert('Usuario no identificado. Por favor, verifica tu sesión e intenta nuevamente.');
    return;
  }
  
  try {
    const processId = `${process.nomenclatura}-${process.descripcion_proceso}`;
    console.log('Process ID generado:', processId);
    console.log('User identifier usado:', userIdentifier);
    
    const isCurrentlyReviewed = processosRevisados.has(processId);
    console.log('Está actualmente revisado:', isCurrentlyReviewed);
    
    if (isCurrentlyReviewed) {
      console.log('Removiendo de revisados...');
      const { error } = await supabase
        .from('user_reviewed_processes')
        .delete()
        .eq('user_id', userIdentifier)
        .eq('process_id', processId)
        .eq('fuente', 'PAC');

      if (error) {
        console.error('Error eliminando:', error);
        throw error;
      }
      
      console.log('Eliminado de DB');
      setProcessosRevisados(prev => {
        const newSet = new Set(prev);
        newSet.delete(processId);
        console.log('Estado actualizado - removido');
        return newSet;
      });
      
    } else {
      console.log('Agregando a revisados...');
      const { data, error } = await supabase
        .from('user_reviewed_processes')
        .insert({
          user_id: userIdentifier,
          process_id: processId,
          nomenclatura: process.nomenclatura,
          fuente: 'PAC'
        });

      if (error) {
        console.error('Error insertando:', error);
        throw error;
      }
      
      console.log('Insertado en DB:', data);
      setProcessosRevisados(prev => {
        const newSet = new Set([...prev, processId]);
        console.log('Estado actualizado - agregado');
        return newSet;
      });
    }
    
  } catch (error) {
    console.error('Error general:', error);
    alert(`Error al actualizar estado: ${error.message}`);
  }
};

  // Función para verificar si un proceso está revisado
  const isProcessReviewed = (process) => {
    const processId = `${process.nomenclatura}-${process.descripcion_proceso}`;
    return processosRevisados.has(processId);
  };

// REEMPLAZAR ReviewCheckbox:
const ReviewCheckbox = ({ process, user, className = "" }) => {
  const isReviewed = isProcessReviewed(process);
  
  const userIdentifier = getUserIdentifier(user);
  const hasValidUser = Boolean(userIdentifier);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Click en ReviewCheckbox PAC:', { user, userIdentifier, hasValidUser });
    
    if (!hasValidUser) {
      alert('Usuario no identificado. Por favor, verifica tu sesión e intenta nuevamente.');
      return;
    }
    
    await toggleProcessReviewed(process, user);
  };

  return (
    <div className={`d-flex flex-column align-items-center gap-1 ${className}`}>
      {/* Checkbox de revisión */}
      <div 
        className="d-flex align-items-center"
        onClick={handleClick}
        style={{ 
          cursor: hasValidUser ? 'pointer' : 'not-allowed',
          padding: '4px',
          minWidth: '40px',
          minHeight: '40px'
        }}
      >
        <input
          type="checkbox"
          checked={isReviewed}
          onChange={() => {}}
          disabled={!hasValidUser}
          style={{
            transform: 'scale(1.2)',
            cursor: hasValidUser ? 'pointer' : 'not-allowed',
            pointerEvents: 'none'
          }}
        />
        {isReviewed && (
          <span 
            className="mb-0 text-success fw-bold ms-2"
            style={{ 
              cursor: 'pointer',
              fontSize: '12px',
              userSelect: 'none'
            }}
          >
            ✓
          </span>
        )}
      </div>
      
      {/* Debug info - quitar en producción */}
      <div style={{ fontSize: '10px', color: '#999', marginLeft: '8px' }}>
        {hasValidUser ? '✓' : '✗'}
      </div>
    </div>
  );
};

  // Componente para estadísticas de revisión
  const ReviewStats = () => {
    const totalProcesos = dataAgrupada.length;
    const procesosRevisadosCount = dataAgrupada.filter(isProcessReviewed).length;
    const procesosPendientes = totalProcesos - procesosRevisadosCount;
    
    return (
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <Badge color="success" style={{ fontSize: '11px', padding: '4px 8px' }}>
          ✅ {procesosRevisadosCount} Revisados
        </Badge>
        <Badge color="warning" style={{ fontSize: '11px', padding: '4px 8px' }}>
          📋 {procesosPendientes} Pendientes
        </Badge>
      </div>
    );
  };

  // Función para verificar si el cache es válido
  const isCacheValid = () => {
    if (!globalPACCache.data || !globalPACCache.timestamp) return false;
    const now = Date.now();
    return (now - globalPACCache.timestamp) < CACHE_DURATION;
  };

  // Función para cargar desde cache o API
  const loadDataIfNeeded = async () => {
    if (isCacheValid() && !globalPACCache.isLoading) {
      console.log('✅ Usando datos PAC desde cache');
      setData(globalPACCache.data);
      setTotalRecords(globalPACCache.totalRecords);
      setDataLoaded(true);
      return;
    }

    if (globalPACCache.isLoading) {
      console.log('⏳ Datos PAC ya se están cargando...');
      if (globalPACCache.data) {
        setData(globalPACCache.data);
        setTotalRecords(globalPACCache.totalRecords);
        setDataLoaded(true);
      }
      return;
    }

    await loadData();
  };

  // Efectos
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      loadDataIfNeeded();
      if (user?.email) {
        loadUserReviewedProcesses(user);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user && processosRevisados.size === 0 && !loadingReviewed) {
      console.log('Usuario cambió, recargando procesos revisados...');
      loadUserReviewedProcesses(user);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownDepartamentoRef.current && !dropdownDepartamentoRef.current.contains(event.target)) {
        setDropdownDepartamento(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para obtener el conteo total de registros
  const getTotalCount = async () => {
    try {
      const { count, error } = await supabase
        .from('pac_vigente')
        .select('*', { count: 'exact', head: true });

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error al obtener conteo total:', error);
      return 0;
    }
  };

  // Función para cargar datos en lotes
  const loadAllDataInBatches = async (totalCount) => {
    const allData = [];
    const totalBatches = Math.ceil(totalCount / BATCH_SIZE);
    
    console.log(`📊 Cargando ${totalCount} registros en ${totalBatches} lotes de ${BATCH_SIZE}`);

    for (let i = 0; i < totalBatches; i++) {
      const start = i * BATCH_SIZE;
      const end = start + BATCH_SIZE - 1;
      
      console.log(`🔄 Cargando lote ${i + 1}/${totalBatches} (registros ${start + 1}-${Math.min(end + 1, totalCount)})`);
      
      try {
        const { data: batchData, error } = await supabase
          .from('pac_vigente')
          .select('*')
          .range(start, end)
          .order('id', { ascending: true });

        if (error) throw error;
        
        if (batchData && batchData.length > 0) {
          allData.push(...batchData);
        }
        
        const progress = Math.round(((i + 1) / totalBatches) * 100);
        setLoadingProgress(progress);
        
        if (i < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
      } catch (error) {
        console.error(`❌ Error en lote ${i + 1}:`, error);
        throw error;
      }
    }

    return allData;
  };

  // Funciones de carga de datos
  const loadData = async (forceRefresh = false) => {
    if (globalPACCache.isLoading && !forceRefresh) {
      console.log('⏸️ Carga PAC ya en progreso, saltando...');
      return;
    }

    setLoading(true);
    setRefreshing(true);
    setError(null);
    setLoadingProgress(0);
    globalPACCache.isLoading = true;

    try {
      console.log('🔄 Iniciando carga completa de datos PAC...');
      
      const totalCount = await getTotalCount();
      console.log(`📊 Total de registros en la tabla: ${totalCount}`);
      
      if (totalCount === 0) {
        setData([]);
        setTotalRecords(0);
        setDataLoaded(true);
        return;
      }

      const allData = await loadAllDataInBatches(totalCount);
      
      console.log(`✅ Datos PAC cargados exitosamente: ${allData.length} registros de ${totalCount} total`);
      
      globalPACCache.data = allData;
      globalPACCache.totalRecords = totalCount;
      globalPACCache.timestamp = Date.now();
      
      setData(allData);
      setTotalRecords(totalCount);
      setDataLoaded(true);
      
    } catch (error) {
      console.error('❌ Error al cargar datos PAC:', error);
      setError('Error al cargar los datos. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingProgress(0);
      globalPACCache.isLoading = false;
    }
  };

  // Función para refrescar datos manualmente
  const refreshData = () => {
    console.log('🔄 Refrescando datos PAC manualmente...');
    loadData(true);
  };

  // Función para agrupar datos por proceso/convocatoria
  const dataAgrupada = useMemo(() => {
    const grupos = {};
    
    data.forEach(item => {
      const clave = `${item.ruc_entidad}_${item.mes_previsto}_${item.descripcion_proceso}_${item.n_referencia}`;
      
      if (!grupos[clave]) {
        grupos[clave] = {
          id: item.id,
          ruc_entidad: item.ruc_entidad,
          entidad: item.entidad,
          departamento: item.departamento,
          provincia: item.provincia,
          distrito: item.distrito,
          descripcion_proceso: item.descripcion_proceso,
          tipoprocesoseleccion: item.tipoprocesoseleccion,
          objetocontractual: item.objetocontractual,
          nomenclatura: item.nomenclatura,
          fecha_publicacion: item.fecha_publicacion,
          mes_previsto: item.mes_previsto,
          fechaConvocatoria: item.fechaConvocatoria,
          fecInicioParticipantes: item.fecInicioParticipantes,
          fecFinParticipantes: item.fecFinParticipantes,
          estado_convocatoria: item.estado_convocatoria,
          ano: item.ano,
          n_referencia: item.n_referencia,
          url_doc: item.url_doc,
          items: [],
          total_items: 0,
          cantidad_total: 0
        };
      }
      
      grupos[clave].items.push({
        n_item: item.n_item,
        descripcion_item: item.descripcion_item,
        cantidad: item.cantidad,
        unidad_medida: item.unidad_medida
      });
      
      grupos[clave].total_items += 1;
      grupos[clave].cantidad_total += parseFloat(item.cantidad || 0);
    });
    
    return Object.values(grupos);
  }, [data]);

  // Obtener valores únicos para filtros
  const departamentosUnicos = useMemo(() => {
    return Array.from(new Set(dataAgrupada.map(item => item.departamento).filter(Boolean))).sort();
  }, [dataAgrupada]);

  const tiposObjetoUnicos = useMemo(() => {
    return Array.from(new Set(dataAgrupada.map(item => item.objetocontractual).filter(Boolean))).sort();
  }, [dataAgrupada]);

  // Filtrar opciones basado en búsqueda
  const departamentosFiltrados = useMemo(() => {
    if (!searchDepartamento.trim()) return departamentosUnicos;
    return departamentosUnicos.filter(dept => 
      dept.toLowerCase().includes(searchDepartamento.toLowerCase())
    );
  }, [departamentosUnicos, searchDepartamento]);

  // Datos filtrados
  const dataFiltrada = useMemo(() => {
    let filtered = dataAgrupada.filter((item) => {
      const coincideDepartamento = departamentosSeleccionados.length === 0 || 
        departamentosSeleccionados.includes(item.departamento);
      
      const coincideDescripcion = filtroDescripcion.trim() === '' || 
        (item.descripcion_proceso || '').toLowerCase().includes(filtroDescripcion.toLowerCase());
      
      const coincideTipoObjeto = filtroTipoObjeto === '' || 
        (item.objetocontractual || '').toLowerCase() === filtroTipoObjeto.toLowerCase();

      const coincideNomenclatura = filtroNomenclatura.trim() === '' || 
        (item.nomenclatura || '').toLowerCase().includes(filtroNomenclatura.toLowerCase());

      const coincideEntidad = filtroEntidad.trim() === '' || 
        (item.entidad || '').toLowerCase().includes(filtroEntidad.toLowerCase());

      const coincideEstadoRevision = 
        filtroEstadoRevision === 'todos' || 
        (filtroEstadoRevision === 'pendientes' && !isProcessReviewed(item)) ||
        (filtroEstadoRevision === 'revisados' && isProcessReviewed(item));

      return coincideDepartamento && 
            coincideDescripcion && coincideTipoObjeto && 
            coincideNomenclatura && coincideEntidad && coincideEstadoRevision;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (sortConfig.key.includes('fecha') || sortConfig.key.includes('Fecha')) {
          const aDate = new Date(aValue);
          const bDate = new Date(bValue);
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
        
        if (sortConfig.key === 'ruc_entidad' || sortConfig.key === 'n_referencia' || sortConfig.key === 'total_items') {
          const aNum = Number(aValue) || 0;
          const bNum = Number(bValue) || 0;
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [dataAgrupada, departamentosSeleccionados, 
      filtroDescripcion, filtroTipoObjeto, filtroNomenclatura, filtroEntidad, filtroEstadoRevision, sortConfig, processosRevisados]);

  // Datos paginados
  const dataPaginada = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return dataFiltrada.slice(start, start + itemsPerPage);
  }, [dataFiltrada, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(dataFiltrada.length / itemsPerPage);

  // Funciones de utilidad
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-PE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2}):(\d{2})/, '$1-$2-$3 $4:$5:$6');
    } catch (error) {
      return '-';
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const clearFilters = () => {
    setDepartamentosSeleccionados([]);
    setFiltroDescripcion('');
    setFiltroTipoObjeto('');
    setFiltroNomenclatura('');
    setFiltroEntidad('');
    setFiltroEstadoRevision('todos');
    setSortConfig({ key: null, direction: 'asc' });
    setSearchDepartamento('');
    setCurrentPage(1);
  };

  const openModal = (contract) => {
    setSelectedContract(contract);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedContract(null);
    setModalOpen(false);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const getTypeColor = (tipo) => {
    const colors = {
      'BIEN': 'primary',
      'SERVICIO': 'success',
      'OBRA': 'warning',
      'CONSULTORÍA': 'info',
      'BIENES': 'primary',
      'SERVICIOS': 'success',
      'OBRAS': 'warning',
      'CONSULTORIA': 'info'
    };
    return colors[tipo?.toUpperCase()] || 'secondary';
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'ACTIVO': 'success',
      'CONVOCADO': 'info',
      'ADJUDICADO': 'primary',
      'DESIERTO': 'warning',
      'CANCELADO': 'danger',
      'EN EVALUACIÓN': 'secondary',
      'PUBLICADO': 'info'
    };
    return colors[estado?.toUpperCase()] || 'light';
  };

  // Funciones para manejar tooltips de descripción
  const handleMouseEnterDescription = (event, description) => {
    if (isMobile) return;
    
    const rect = event.target.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setHoveredDescription(description);
  };

  const handleMouseLeaveDescription = () => {
    setHoveredDescription(null);
  };

  // Funciones para manejar dropdowns
  const createDropdownHandlers = (
    selectedItems,
    setSelectedItems,
    setDropdownOpen,
    searchValue,
    setSearchValue
  ) => ({
    toggle: (item, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setSelectedItems(prev => 
        prev.includes(item) 
          ? prev.filter(i => i !== item)
          : [...prev, item]
      );
    },
    selectAll: (allItems, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setSelectedItems(allItems);
    },
    clear: (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setSelectedItems([]);
    },
    getButtonText: (allItems) => {
      if (selectedItems.length === 0) return isMobile ? '📋 Todos' : '📋 Todos los departamentos';
      if (selectedItems.length === 1) return isMobile ? `📋 ${selectedItems[0].substring(0, 10)}...` : `📋 ${selectedItems[0]}`;
      return isMobile ? `📋 ${selectedItems.length} sel.` : `📋 ${selectedItems.length} seleccionados`;
    }
  });

  const departamentoHandlers = createDropdownHandlers(
    departamentosSeleccionados,
    setDepartamentosSeleccionados,
    setDropdownDepartamento,
    searchDepartamento,
    setSearchDepartamento
  );

  // Componente de dropdown reutilizable
  const CustomDropdown = ({ 
    isOpen, 
    toggle, 
    buttonText, 
    items, 
    filteredItems, 
    selectedItems, 
    searchValue, 
    setSearchValue, 
    handlers, 
    dropdownRef, 
    placeholder,
    icon = "📋"
  }) => (
    <div ref={dropdownRef} style={{ position: 'relative', zIndex: 1000 }}>
      <Dropdown 
        isOpen={isOpen} 
        toggle={() => {}}
        className="w-100" 
        direction="down"
      >
        <DropdownToggle 
          caret 
          className="form-select-modern w-100 d-flex justify-content-between align-items-center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle(e);
          }}
          size={isMobile ? 'sm' : 'md'}
          style={{ 
            textAlign: 'left',
            backgroundColor: '#f8f9fa',
            border: '2px solid #e9ecef',
            borderRadius: '12px',
            padding: isMobile ? '8px 12px' : '12px 16px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            color: '#495057'
          }}
        >
          <span className="text-truncate">{buttonText}</span>
        </DropdownToggle>
        <DropdownMenu 
          className="w-100"
          style={{ 
            maxHeight: isMobile ? '60vh' : '350px',
            overflowY: 'auto',
            border: '2px solid #e9ecef',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            position: 'absolute',
            zIndex: 9999,
            backgroundColor: '#ffffff',
            minWidth: '100%',
            left: 0,
            right: 'auto',
            transform: 'none',
            marginTop: '4px'
          }}
        >
          <div 
            className="p-3 border-bottom bg-light"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Input
              type="text"
              placeholder={placeholder}
              value={searchValue}
              onChange={(e) => {
                e.stopPropagation();
                setSearchValue(e.target.value);
              }}
              className="form-control-sm"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onFocus={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onInput={(e) => {
                e.stopPropagation();
              }}
              onKeyUp={(e) => {
                e.stopPropagation();
              }}
              onBlur={(e) => {
                e.preventDefault();
              }}
              style={{
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                fontSize: isMobile ? '12px' : '13px',
                color: '#495057'
              }}
              autoComplete="off"
              autoFocus={isOpen}
            />
          </div>
          
          <div className="p-2 border-bottom bg-light">
            <div className="d-flex justify-content-between">
              <Button
                color="link"
                size="sm"
                className="p-1 text-decoration-none fw-bold text-success"
                onClick={(e) => handlers.selectAll(items, e)}
                style={{ fontSize: isMobile ? '11px' : '12px' }}
              >
                ✅ {isMobile ? 'Todos' : 'Seleccionar Todos'}
              </Button>
              <Button
                color="link"
                size="sm"
                className="p-1 text-decoration-none fw-bold text-danger"
                onClick={(e) => handlers.clear(e)}
                style={{ fontSize: isMobile ? '11px' : '12px' }}
              >
                🗑️ Limpiar
              </Button>
            </div>
          </div>

          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <DropdownItem
                key={item}
                className="d-flex align-items-center justify-content-between py-2 px-3"
                onClick={(e) => handlers.toggle(item, e)}
                style={{ 
                  cursor: 'pointer',
                  fontSize: isMobile ? '12px' : '13px',
                  transition: 'background-color 0.2s ease',
                  color: '#495057'
                }}
                toggle={false}
              >
                <span className="text-truncate me-2" style={{ fontWeight: '500', color: '#495057' }}>
                  {isMobile && item.length > 20 ? `${item.substring(0, 20)}...` : item}
                </span>
                {selectedItems.includes(item) && (
                  <span className="text-success fw-bold">✓</span>
                )}
              </DropdownItem>
            ))
          ) : (
            <DropdownItem disabled>
              <span className="text-muted fst-italic">No se encontraron resultados</span>
            </DropdownItem>
          )}
          
          <div className="p-3 border-top text-center bg-light">
            <Button
              color="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
              style={{
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: isMobile ? '11px' : '12px',
                padding: isMobile ? '6px 12px' : '8px 16px'
              }}
            >
              ✅ Aplicar Filtros
            </Button>
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );

  // Componente de paginación
  const PaginationComponent = () => {
    const getPageNumbers = () => {
      const pages = [];
      const maxPagesToShow = isMobile ? 3 : 5;
      
      if (totalPages <= maxPagesToShow) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        
        if (endPage - startPage + 1 < maxPagesToShow) {
          startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }
      }
      
      return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
      <div className={`d-flex ${isMobile ? 'flex-column' : 'flex-lg-row'} justify-content-between align-items-center mt-4 gap-3`}>
        <div className="d-flex flex-column flex-lg-row align-items-center gap-3">
          <div className="text-muted fw-medium text-center" style={{ fontSize: isMobile ? '12px' : '14px' }}>
            {isMobile ? 
              `${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, dataFiltrada.length)} de ${dataFiltrada.length}` :
              `Mostrando ${((currentPage - 1) * itemsPerPage) + 1} a ${Math.min(currentPage * itemsPerPage, dataFiltrada.length)} de ${dataFiltrada.length} procesos`
            }
          </div>
          <div className="d-flex align-items-center gap-2">
            <Label className="text-muted fw-medium mb-0" style={{ fontSize: isMobile ? '12px' : '13px' }}>
              {isMobile ? '' : 'Mostrar:'}
            </Label>
            <Input
              type="select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              size={isMobile ? 'sm' : 'md'}
              style={{
                width: isMobile ? '60px' : '80px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                fontSize: isMobile ? '12px' : '13px'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Input>
          </div>
        </div>
        
        <Pagination className="mb-0" size={isMobile ? 'sm' : ''}>
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink 
              previous 
              onClick={() => setCurrentPage(currentPage - 1)}
              style={{
                borderRadius: '8px 0 0 8px',
                border: '1px solid #dee2e6',
                color: '#6c757d',
                fontSize: isMobile ? '12px' : '14px'
              }}
            />
          </PaginationItem>
          
          {pageNumbers.map(page => (
            <PaginationItem key={page} active={page === currentPage}>
              <PaginationLink 
                onClick={() => setCurrentPage(page)}
                style={{
                  border: '1px solid #dee2e6',
                  color: page === currentPage ? '#fff' : '#6c757d',
                  backgroundColor: page === currentPage ? '#0d6efd' : '#fff',
                  fontWeight: page === currentPage ? '600' : '500',
                  fontSize: isMobile ? '12px' : '14px'
                }}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          ))}
          
          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink 
              next 
              onClick={() => setCurrentPage(currentPage + 1)}
              style={{
                borderRadius: '0 8px 8px 0',
                border: '1px solid #dee2e6',
                color: '#6c757d',
                fontSize: isMobile ? '12px' : '14px'
              }}
            />
          </PaginationItem>
        </Pagination>
      </div>
    );
  };

  // Tooltip personalizado para descripciones
  const DescriptionTooltip = () => {
    if (!hoveredDescription || isMobile) return null;

    return (
      <div
        style={{
          position: 'fixed',
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          transform: 'translateX(-50%) translateY(-100%)',
          backgroundColor: '#343a40',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '13px',
          lineHeight: '1.4',
          maxWidth: '400px',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid #495057',
          fontWeight: '500'
        }}
      >
        {hoveredDescription}
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid #343a40'
          }}
        />
      </div>
    );
  };

  // Componente de tarjeta móvil para cada contrato
  const MobileContractCard = ({ contrato, index }) => (
    <Card 
      className={`mb-3 shadow-sm border-0 ${isProcessReviewed(contrato) ? 'border-success' : ''}`}
      style={{ 
        borderRadius: '16px',
        backgroundColor: isProcessReviewed(contrato) ? '#f8fff8' : (index % 2 === 0 ? '#ffffff' : '#f8f9fa'),
        borderLeft: isProcessReviewed(contrato) ? '4px solid #28a745' : 'none'
      }}
    >
      <CardBody className="p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center gap-2">
            <Badge 
              color={getTypeColor(contrato.objetocontractual)}
              style={{
                fontSize: '11px',
                fontWeight: '600',
                padding: '4px 8px',
                borderRadius: '12px'
              }}
            >
              {contrato.objetocontractual || 'N/A'}
            </Badge>
          </div>
          <div className="d-flex flex-column align-items-end gap-2">
            <Badge 
              color="warning"
              style={{
                fontSize: '11px',
                fontWeight: '600',
                padding: '4px 8px',
                borderRadius: '12px'
              }}
            >
              {contrato.total_items} items
            </Badge>
            <ReviewCheckbox 
              process={contrato} 
              user={user}
              className="justify-content-end"
            />
          </div>
        </div>
        
        <h6 className="fw-bold text-primary mb-1" style={{ fontSize: '14px' }}>
          {contrato.nomenclatura || 'Sin nomenclatura'}
        </h6>
        
        <p className="text-muted mb-2" style={{ fontSize: '12px', lineHeight: '1.4' }}>
          <strong>Entidad:</strong> {contrato.entidad || 'N/A'}
        </p>
        
        <p className="text-muted mb-2" style={{ fontSize: '12px', lineHeight: '1.4' }}>
          <strong>RUC:</strong> {contrato.ruc_entidad || 'N/A'}
        </p>
        
        <p className="text-dark mb-2" style={{ fontSize: '13px', lineHeight: '1.4' }}>
          {contrato.descripcion_proceso ? 
            (contrato.descripcion_proceso.length > 120 ? 
              `${contrato.descripcion_proceso.substring(0, 120)}...` : 
              contrato.descripcion_proceso
            ) : 
            'Sin descripción'
          }
        </p>
        
        <div className="mb-2" style={{ fontSize: '12px' }}>
          <div className="d-flex justify-content-between text-muted">
            <span><strong>Ubicación:</strong></span>
            <span>{contrato.departamento}, {contrato.provincia}</span>
          </div>
        </div>
        
        <div className="mb-3" style={{ fontSize: '12px' }}>
          <div className="d-flex justify-content-between text-muted">
            <span><strong>Tipo Proceso:</strong></span>
            <span>{contrato.tipoprocesoseleccion || 'N/A'}</span>
          </div>
        </div>
        
        <div className="mb-3" style={{ fontSize: '11px', color: '#6c757d' }}>
          <div className="row g-1">
            <div className="col-6">
              <strong>F. Inicio:</strong><br />
              {formatDate(contrato.fecInicioParticipantes)}
            </div>
            <div className="col-6">
              <strong>F. Fin:</strong><br />
              {formatDate(contrato.fecFinParticipantes)}
            </div>
          </div>
        </div>
        
        <div className="d-flex justify-content-between align-items-center gap-2">
          <Button
            color="primary"
            size="sm"
            onClick={() => openModal(contrato)}
            style={{
              borderRadius: '8px',
              fontWeight: '600',
              padding: '6px 12px',
              fontSize: '11px',
              flex: 1
            }}
          >
            Ver Detalles
          </Button>
          
          {contrato.url_doc && (
            <Button
              color="warning"
              size="sm"
              onClick={() => window.open(contrato.url_doc, '_blank')}
              style={{
                borderRadius: '8px',
                fontWeight: '600',
                padding: '6px 12px',
                fontSize: '11px',
                flex: 1
              }}
            >
              Ver Bases
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );

  return (
    <Container fluid className={`py-3 px-2 px-md-4 ${isMobile ? 'mobile' : ''} ${isTablet ? 'tablet' : ''}`} style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Tooltip personalizado */}
      <DescriptionTooltip />
      
      {/* Header Superior */}
      <Card className="shadow-lg border-0 mb-4" style={{ borderRadius: isMobile ? '12px' : '20px', background: 'linear-gradient(135deg, #6f42c1 0%, #764ba2 100%)' }}>
        <CardBody className={`${isMobile ? 'p-3' : 'p-4'}`}>
          <Row className="align-items-center">
            <Col xs={12} md={8}>
              <div className={`d-flex align-items-center ${isMobile ? 'flex-column text-center' : 'flex-row'} mb-2`}>
                <div className={`${isMobile ? 'mb-2' : 'me-3'}`} style={{ fontSize: isMobile ? '2rem' : '3rem' }}>
                  📊
                </div>
                <div>
                  <h1 className="mb-1 fw-bold text-white" style={{ fontSize: isMobile ? '1.5rem' : '2.5rem' }}>
                    {isMobile ? 'PAC Vigentes' : 'Dashboard PAC - Procesos Vigentes'}
                  </h1>
                  <p className="mb-0 text-white-50" style={{ fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
                    {isMobile ? 'Procesos en curso' : 'Procesos en curso'} • 
                    <Badge color="light" className="ms-2 text-primary fw-bold" style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      {dataFiltrada.length} {isMobile ? 'encontrados' : 'procesos'}
                    </Badge>
                  </p>
                  {/* Estadísticas de revisión */}
                  {data.length > 0 && <ReviewStats />}
                </div>
              </div>
            </Col>
            <Col xs={12} md={4} className={`${isMobile ? 'text-center mt-3' : 'text-end'}`}>
              <Button 
                color="light" 
                onClick={refreshData}
                disabled={loading}
                size={isMobile ? 'sm' : 'md'}
                style={{
                  borderRadius: '12px',
                  fontWeight: '600',
                  padding: isMobile ? '8px 16px' : '12px 24px',
                  fontSize: isMobile ? '13px' : '14px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(255,255,255,0.3)'
                }}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {isMobile ? 'Cargando...' : 'Cargando...'}
                  </>
                ) : (
                  <>
                    🔄 {isMobile ? 'Actualizar' : 'Actualizar'}
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Alertas de error */}
      {error && (
        <Alert color="danger" className="mb-4" style={{ borderRadius: '12px' }}>
          <h6 className="alert-heading fw-bold">⚠️ Error de carga</h6>
          {error}
        </Alert>
      )}

      {/* Barra de progreso durante la carga */}
      {loading && (
        <Card className="mb-4" style={{ borderRadius: '12px' }}>
          <CardBody className="p-3">
            <div className="text-center mb-2">
              <small className="text-muted fw-medium" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                Cargando datos completos... {loadingProgress > 0 && `${loadingProgress}%`}
              </small>
              {totalRecords > 0 && (
                <small className="d-block text-muted" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                  Procesando {data.length} de {totalRecords} registros
                </small>
              )}
            </div>
            <Progress 
              animated 
              value={loadingProgress || 100} 
              style={{ height: '8px', borderRadius: '4px' }}
              color={loadingProgress > 0 ? 'primary' : 'info'}
            />
          </CardBody>
        </Card>
      )}

      {/* Mensaje cuando no hay datos pero no está cargando */}
      {!loading && data.length === 0 && !error && (
        <Card className="empty-data-card mb-4" style={{ borderRadius: '12px' }}>
          <CardBody className="text-center p-5">
            <div className="empty-data-content">
              <div className="empty-data-icon mb-3" style={{ fontSize: '4rem' }}>📭</div>
              <h5>No hay datos cargados</h5>
              <p className="text-muted">{isMobile ? 'Haz clic en "Actualizar" para cargar todos los datos PAC' : 'Haz clic en "Actualizar" para cargar todos los datos PAC'}</p>
              <Button color="primary" onClick={refreshData} size={isMobile ? 'sm' : 'md'}>
                🔄 {isMobile ? 'Cargar Datos' : 'Cargar Datos'}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Solo mostrar filtros y contenido si hay datos */}
      {data.length > 0 && (
        <>
          {/* Botón de filtros para móvil */}
          {isMobile && (
            <div className="mb-3">
              <Button
                color="primary"
                onClick={() => setShowFilters(!showFilters)}
                className="w-100 d-flex justify-content-between align-items-center"
                style={{
                  borderRadius: '12px',
                  fontWeight: '600',
                  padding: '12px 16px'
                }}
              >
                <span>🔍 Filtros de Búsqueda</span>
                <span>{showFilters ? '⬆️' : '⬇️'}</span>
              </Button>
            </div>
          )}

          {/* Filtros */}
          <Collapse isOpen={!isMobile || showFilters}>
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '16px' }}>
              <CardBody className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="mb-0 fw-bold text-dark d-flex align-items-center">
                    <span className="me-2" style={{ fontSize: '1.2rem' }}>🔍</span>
                    {isMobile ? 'Filtros de Búsqueda' : 'Filtros de Búsqueda'}
                  </h6>
                  <Button 
                    color="outline-danger" 
                    size="sm" 
                    onClick={clearFilters}
                    style={{
                      borderRadius: '8px',
                      fontWeight: '600',
                      padding: '6px 12px',
                      fontSize: '11px'
                    }}
                  >
                    🗑️ Limpiar
                  </Button>
                </div>

                <Row className="gy-3">
                  <Col xs={12} lg={4}>
                    <FormGroup>
                      <Label className="fw-bold text-dark mb-2 d-flex align-items-center">
                        <span className="me-2">📍</span>
                        Departamento
                      </Label>
                      <CustomDropdown
                        isOpen={dropdownDepartamento}
                        toggle={(e) => {
                          if (e && e.target) {
                            const isInputArea = e.target.tagName === 'INPUT' || 
                                               e.target.closest('.p-3.border-bottom.bg-light') ||
                                               e.target.classList.contains('form-control-sm');
                            
                            if (isInputArea) {
                              return;
                            }
                          }
                          
                          setDropdownDepartamento(!dropdownDepartamento);
                        }}
                        buttonText={departamentoHandlers.getButtonText(departamentosUnicos)}
                        items={departamentosUnicos}
                        filteredItems={departamentosFiltrados}
                        selectedItems={departamentosSeleccionados}
                        searchValue={searchDepartamento}
                        setSearchValue={setSearchDepartamento}
                        handlers={departamentoHandlers}
                        dropdownRef={dropdownDepartamentoRef}
                        placeholder="🔍 Buscar departamento..."
                        icon="📍"
                      />
                    </FormGroup>
                  </Col>

                  <Col xs={12} lg={4}>
                    <FormGroup>
                      <Label className="fw-bold text-dark mb-2 d-flex align-items-center">
                        <span className="me-2">🏛️</span>
                        Entidad
                      </Label>
                      <InputGroup style={{ borderRadius: '12px', overflow: 'hidden' }}>
                        <InputGroupText style={{ 
                          backgroundColor: '#e9ecef', 
                          border: '2px solid #e9ecef',
                          borderRight: 'none',
                          fontSize: '1rem'
                        }}>
                          🏛️
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Ej: Ministerio, Municipalidad..."
                          value={filtroEntidad}
                          onChange={(e) => setFiltroEntidad(e.target.value)}
                          size={isMobile ? 'sm' : 'md'}
                          style={{
                            border: '2px solid #e9ecef',
                            borderLeft: 'none',
                            borderRadius: '0 12px 12px 0',
                            fontSize: isMobile ? '13px' : '14px',
                            fontWeight: '500',
                            padding: isMobile ? '8px 12px' : '12px 16px'
                          }}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>

                  <Col xs={12} lg={4}>
                    <FormGroup>
                      <Label className="fw-bold text-dark mb-2 d-flex align-items-center">
                        <span className="me-2">📊</span>
                        Estado de Revisión
                      </Label>
                      <Input
                        type="select"
                        value={filtroEstadoRevision}
                        onChange={(e) => setFiltroEstadoRevision(e.target.value)}
                        size={isMobile ? 'sm' : 'md'}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e9ecef',
                          fontSize: isMobile ? '13px' : '14px',
                          fontWeight: '500',
                          padding: isMobile ? '8px 12px' : '12px 16px',
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        <option value="todos">{isMobile ? '📋 Todos' : '📋 Mostrar todos'}</option>
                        <option value="pendientes">{isMobile ? '⏳ Pendientes' : '⏳ Solo pendientes'}</option>
                        <option value="revisados">{isMobile ? '✅ Revisados' : '✅ Solo revisados'}</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col xs={12} lg={6}>
                    <FormGroup>
                      <Label className="fw-bold text-dark mb-2 d-flex align-items-center">
                        <span className="me-2">📝</span>
                        Descripción Proceso
                      </Label>
                      <InputGroup style={{ borderRadius: '12px', overflow: 'hidden' }}>
                        <InputGroupText style={{ 
                          backgroundColor: '#e9ecef', 
                          border: '2px solid #e9ecef',
                          borderRight: 'none',
                          fontSize: '1rem'
                        }}>
                          🔍
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Ej: mantenimiento, salud, equipos..."
                          value={filtroDescripcion}
                          onChange={(e) => setFiltroDescripcion(e.target.value)}
                          size={isMobile ? 'sm' : 'md'}
                          style={{
                            border: '2px solid #e9ecef',
                            borderLeft: 'none',
                            borderRadius: '0 12px 12px 0',
                            fontSize: isMobile ? '13px' : '14px',
                            fontWeight: '500',
                            padding: isMobile ? '8px 12px' : '12px 16px'
                          }}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>

                  <Col xs={12} lg={6}>
                    <FormGroup>
                      <Label className="fw-bold text-dark mb-2 d-flex align-items-center">
                        <span className="me-2">📦</span>
                        Tipo de Objeto
                      </Label>
                      <Input
                        type="select"
                        value={filtroTipoObjeto}
                        onChange={(e) => setFiltroTipoObjeto(e.target.value)}
                        size={isMobile ? 'sm' : 'md'}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e9ecef',
                          fontSize: isMobile ? '13px' : '14px',
                          fontWeight: '500',
                          padding: isMobile ? '8px 12px' : '12px 16px',
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        <option value="">{isMobile ? '📋 Todos' : '📋 Todos los tipos'}</option>
                        {tiposObjetoUnicos.map((tipo) => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col xs={12} lg={6}>
                    <FormGroup>
                      <Label className="fw-bold text-dark mb-2 d-flex align-items-center">
                        <span className="me-2">🏷️</span>
                        Nomenclatura
                      </Label>
                      <InputGroup style={{ borderRadius: '12px', overflow: 'hidden' }}>
                       <Input
                          type="text"
                          placeholder="Ej: CP-1-2024"
                          value={filtroNomenclatura}
                          onChange={(e) => setFiltroNomenclatura(e.target.value)}
                          size={isMobile ? 'sm' : 'md'}
                          style={{
                            border: '2px solid #e9ecef',
                            borderRadius: '12px',
                            fontSize: isMobile ? '13px' : '14px',
                            fontWeight: '500',
                            padding: isMobile ? '8px 12px' : '12px 16px'
                          }}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Collapse>

          {/* Contenido Principal */}
          <Card className="shadow-sm border-0" style={{ borderRadius: '16px' }}>
            <CardBody className="p-0">
              {dataPaginada.length > 0 ? (
                <>
                  {isMobile ? (
                    /* Vista de tarjetas para móvil */
                    <div className="p-3">
                      {dataPaginada.map((contrato, index) => (
                        <MobileContractCard key={`${contrato.ruc_entidad}-${contrato.mes_previsto}-${index}`} contrato={contrato} index={index} />
                      ))}
                    </div>
                  ) : (
                    /* Vista de tabla para desktop y tablet */
                    <div style={{ 
                      overflowX: 'auto',
                      borderRadius: '16px 16px 0 0'
                    }}>
                      <Table responsive hover className="mb-0" style={{ fontSize: isTablet ? '12px' : '13px', minWidth: isTablet ? '1200px' : '1400px' }}>
                        <thead style={{ 
                          background: 'linear-gradient(135deg, #6f42c1 0%, #764ba2 100%)',
                          color: 'white'
                        }}>
                          <tr>
                            {[
                              { key: 'revisado', label: 'REVISADO', width: '100px' },
                              { key: 'nomenclatura', label: 'NOMENCLATURA', width: isTablet ? '120px' : '140px' },
                              { key: 'departamento', label: 'DEPARTAMENTO', width: isTablet ? '110px' : '120px' },
                              { key: 'provincia', label: 'PROVINCIA', width: isTablet ? '110px' : '120px' },
                              ...(isTablet ? [] : [{ key: 'distrito', label: 'DISTRITO', width: '120px' }]),
                              { key: 'entidad', label: 'ENTIDAD', width: isTablet ? '180px' : '200px' },
                              { key: 'descripcion_proceso', label: 'DESCRIPCIÓN', width: isTablet ? '250px' : '300px' },
                              { key: 'objetocontractual', label: 'TIPO OBJETO', width: isTablet ? '100px' : '110px' },
                              { key: 'total_items', label: 'ITEMS', width: '70px' },
                              { key: 'fecInicioParticipantes', label: 'F. INICIO', width: '110px' },
                              { key: 'fecFinParticipantes', label: 'F. FIN', width: '110px' }
                            ].map((col) => (
                              <th
                                key={col.key} 
                                onClick={() => col.key !== 'revisado' && handleSort(col.key)}
                                style={{ 
                                  cursor: col.key !== 'revisado' ? 'pointer' : 'default', 
                                  userSelect: 'none',
                                  fontWeight: '700',
                                  fontSize: isTablet ? '11px' : '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  padding: isTablet ? '12px 8px' : '14px 10px',
                                  borderBottom: 'none',
                                  minWidth: col.width,
                                  position: 'sticky',
                                  top: 0,
                                  zIndex: 10,
                                  background: 'linear-gradient(135deg, #6f42c1 0%, #764ba2 100%)',
                                  textAlign: col.key === 'revisado' ? 'center' : 'left'
                                }}
                                className="text-white"
                              >
                                <div className="d-flex align-items-center justify-content-between">
                                  <span>{col.label}</span>
                                  {col.key !== 'revisado' && (
                                    <span style={{ 
                                      fontSize: '14px', 
                                      marginLeft: '6px',
                                      opacity: sortConfig.key === col.key ? 1 : 0.5
                                    }}>
                                      {getSortIcon(col.key)}
                                    </span>
                                  )}
                                </div>
                              </th>
                            ))}
                            <th 
                              className="text-center text-white"
                              style={{
                                fontWeight: '700',
                                fontSize: isTablet ? '11px' : '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                borderBottom: 'none',
                                width: isTablet ? '100px' : '130px',
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                background: 'linear-gradient(135deg, #6f42c1 0%, #764ba2 100%)'
                              }}
                            >
                              ACCIONES
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {dataPaginada.map((contrato, index) => (
                            <tr 
                              key={`${contrato.ruc_entidad}-${contrato.mes_previsto}-${index}`}
                              style={{
                                backgroundColor: isProcessReviewed(contrato) 
                                  ? '#f8fff8' 
                                  : (index % 2 === 0 ? '#ffffff' : '#f8f9fa'),
                                transition: 'all 0.3s ease',
                                borderLeft: isProcessReviewed(contrato) ? '4px solid #28a745' : 'none'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#e3f2fd';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isProcessReviewed(contrato) 
                                  ? '#f8fff8' 
                                  : (index % 2 === 0 ? '#ffffff' : '#f8f9fa');
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              {/* REVISADO - PRIMERA COLUMNA */}
                              <td className="text-center" style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <ReviewCheckbox 
                                  process={contrato} 
                                  user={user}
                                  className="justify-content-center"
                                />
                              </td>

                              {/* NOMENCLATURA */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                fontWeight: '600',
                                color: '#6f42c1',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <div style={{
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  lineHeight: '1.4'
                                }}>
                                  {contrato.nomenclatura || '-'}
                                </div>
                              </td>
                              
                              {/* DEPARTAMENTO */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                fontWeight: '500',
                                color: '#6c757d',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <div style={{
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  lineHeight: '1.4'
                                }}>
                                  {contrato.departamento || '-'}
                                </div>
                              </td>
                              
                              {/* PROVINCIA */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                fontWeight: '500',
                                color: '#6c757d',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <div style={{
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  lineHeight: '1.4'
                                }}>
                                  {contrato.provincia || '-'}
                                </div>
                              </td>
                              
                              {/* DISTRITO (solo desktop) */}
                              {!isTablet && (
                                <td style={{ 
                                  padding: '14px 10px',
                                  fontWeight: '500',
                                  color: '#6c757d',
                                  borderBottom: '1px solid #e9ecef'
                                }}>
                                  <div style={{
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal',
                                    lineHeight: '1.4'
                                  }}>
                                    {contrato.distrito || '-'}
                                  </div>
                                </td>
                              )}
                              
                              {/* ENTIDAD */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <div style={{
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  fontWeight: '500',
                                  color: '#495057',
                                  lineHeight: '1.4',
                                  maxWidth: isTablet ? '180px' : '200px'
                                }}>
                                  {contrato.entidad ? 
                                    (isTablet && contrato.entidad.length > 30 ? 
                                      `${contrato.entidad.substring(0, 30)}...` : 
                                      contrato.entidad
                                    ) : '-'
                                  }
                                </div>
                              </td>
                              
                              {/* DESCRIPCIÓN */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <div
                                  style={{
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal',
                                    fontWeight: '500',
                                    color: '#495057',
                                    lineHeight: '1.4',
                                    maxWidth: isTablet ? '250px' : '300px',
                                    cursor: 'help'
                                  }}
                                  onMouseEnter={(e) => handleMouseEnterDescription(e, contrato.descripcion_proceso)}
                                  onMouseLeave={handleMouseLeaveDescription}
                                >
                                  {contrato.descripcion_proceso ? 
                                    (isTablet && contrato.descripcion_proceso.length > 50 ? 
                                      `${contrato.descripcion_proceso.substring(0, 50)}...` : 
                                      contrato.descripcion_proceso
                                    ) : '-'
                                  }
                                </div>
                              </td>
                              
                              {/* TIPO OBJETO */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <Badge 
                                  color={getTypeColor(contrato.objetocontractual)}
                                  style={{
                                    fontSize: isTablet ? '10px' : '11px',
                                    fontWeight: '600',
                                    padding: isTablet ? '4px 8px' : '5px 10px',
                                    borderRadius: '15px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}
                                >
                                  {contrato.objetocontractual || 'N/A'}
                                </Badge>
                              </td>
                              
                              {/* TOTAL ITEMS */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                fontWeight: '600',
                                color: '#28a745',
                                borderBottom: '1px solid #e9ecef',
                                textAlign: 'center'
                              }}>
                                <Badge 
                                  color="success"
                                  style={{
                                    fontSize: isTablet ? '10px' : '11px',
                                    fontWeight: '700',
                                    padding: isTablet ? '5px 8px' : '6px 10px',
                                    borderRadius: '15px'
                                  }}
                                >
                                  {contrato.total_items}
                                </Badge>
                              </td>
                              
                              {/* F. INICIO */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                fontWeight: '500',
                                color: '#495057',
                                borderBottom: '1px solid #e9ecef',
                                fontSize: isTablet ? '11px' : '12px'
                              }}>
                                {formatDate(contrato.fecInicioParticipantes)}
                              </td>
                              
                              {/* F. FIN */}
                              <td style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                fontWeight: '500',
                                color: '#495057',
                                borderBottom: '1px solid #e9ecef',
                                fontSize: isTablet ? '11px' : '12px'
                              }}>
                                {formatDate(contrato.fecFinParticipantes)}
                              </td>
                              
                              {/* ACCIONES */}
                              <td className="text-center" style={{ 
                                padding: isTablet ? '12px 8px' : '14px 10px',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <div className="d-flex flex-column gap-1 justify-content-center align-items-center">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => openModal(contrato)}
                                    title="Ver detalles completos"
                                    style={{
                                      borderRadius: '6px',
                                      fontWeight: '600',
                                      padding: isTablet ? '4px 8px' : '6px 10px',
                                      fontSize: isTablet ? '10px' : '11px',
                                      border: 'none',
                                      boxShadow: '0 2px 6px rgba(13,110,253,0.3)'
                                    }}
                                  >
                                    👁️ Ver
                                  </Button>
                                  
                                  {contrato.url_doc && (
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => window.open(contrato.url_doc, '_blank')}
                                      title="Ver las bases"
                                      style={{
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        padding: isTablet ? '4px 8px' : '6px 10px',
                                        fontSize: isTablet ? '10px' : '11px',
                                        border: 'none',
                                        boxShadow: '0 2px 6px rgba(255,193,7,0.3)'
                                      }}
                                    >
                                      📄 Bases
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                  
                  <div className="px-4 pb-4 bg-light" style={{ borderRadius: '0 0 16px 16px' }}>
                    <PaginationComponent />
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <div className="mb-4" style={{ fontSize: '4rem', opacity: 0.3 }}>📄</div>
                  <h4 className="text-muted mb-3 fw-bold" style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                    No se encontraron procesos
                  </h4>
                  <p className="text-muted mb-4" style={{ fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
                    {isMobile ? 'Ajusta los filtros para encontrar más resultados' : 'Intenta ajustar los filtros de búsqueda para encontrar más resultados'}
                  </p>
                  <Button 
                    color="primary" 
                    onClick={clearFilters}
                    size={isMobile ? 'sm' : ''}
                    style={{
                      borderRadius: '12px',
                      fontWeight: '600',
                      padding: isMobile ? '8px 16px' : '12px 24px',
                      fontSize: isMobile ? '12px' : '14px',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(13,110,253,0.3)'
                    }}
                  >
                    🗑️ {isMobile ? 'Limpiar Filtros' : 'Limpiar Todos los Filtros'}
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {/* Modal de Detalles */}
      <Modal 
        isOpen={modalOpen} 
        toggle={closeModal} 
        size={isMobile ? 'sm' : 'xl'}
        backdrop="static"
        keyboard={false}
        fullscreen={isMobile}
        style={{ zIndex: 1050 }}
      >
        <ModalHeader 
          toggle={closeModal} 
          className="border-0 pb-0"
          style={{
            background: 'linear-gradient(135deg, #6f42c1 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: isMobile ? '0' : '20px 20px 0 0'
          }}
        >
          <div className={`d-flex align-items-center ${isMobile ? 'flex-column text-center' : 'flex-row'}`}>
            <div className={`${isMobile ? 'mb-2' : 'me-2'}`} style={{ fontSize: isMobile ? '2rem' : '2.5rem' }}>
              📋
            </div>
            <div>
              <h4 className="mb-1 fw-bold text-white" style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                {isMobile ? 'Detalles del Proceso' : 'Detalles del Proceso PAC'}
              </h4>
              <p className="mb-0 text-white-50" style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                {selectedContract?.nomenclatura || 'Sin nomenclatura disponible'} • {selectedContract?.total_items || 0} items
              </p>
            </div>
          </div>
        </ModalHeader>
        
        <ModalBody className={`${isMobile ? 'p-2' : 'p-4'}`} style={{ backgroundColor: '#f8f9fa' }}>
          {selectedContract && (
            <Row className={`${isMobile ? 'gy-2' : 'gy-3'}`}>
              {/* Información de la Entidad */}
              <Col xs={12} md={6}>
                <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <CardBody className={`${isMobile ? 'p-3' : 'p-4'}`}>
                    <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: '#0d6efd', fontSize: isMobile ? '1rem' : '1.1rem' }}>
                      <span className="me-2" style={{ fontSize: '1.2rem' }}>🏢</span>
                      Información de la Entidad
                    </h6>
                    <div className="space-y-2">
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>RUC:</strong>
                        <span className="fw-bold text-dark" style={{ fontSize: isMobile ? '12px' : '14px' }}>{selectedContract.ruc_entidad || '-'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-start py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Entidad:</strong>
                        <span className="fw-medium text-dark text-end" style={{ maxWidth: '60%', fontSize: isMobile ? '12px' : '14px' }}>
                          {selectedContract.entidad || '-'}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Departamento:</strong>
                        <span className="fw-medium text-dark" style={{ fontSize: isMobile ? '12px' : '14px' }}>{selectedContract.departamento || '-'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Provincia:</strong>
                        <span className="fw-medium text-dark" style={{ fontSize: isMobile ? '12px' : '14px' }}>{selectedContract.provincia || '-'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Distrito:</strong>
                        <span className="fw-medium text-dark" style={{ fontSize: isMobile ? '12px' : '14px' }}>{selectedContract.distrito || '-'}</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Información del Proceso */}
              <Col xs={12} md={6}>
                <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <CardBody className={`${isMobile ? 'p-3' : 'p-4'}`}>
                    <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: '#28a745', fontSize: isMobile ? '1rem' : '1.1rem' }}>
                      <span className="me-2" style={{ fontSize: '1.2rem' }}>⚙️</span>
                      Información del Proceso
                    </h6>
                    <div className="space-y-2">
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Nomenclatura:</strong>
                        <span className="fw-bold text-primary" style={{ fontSize: isMobile ? '12px' : '14px' }}>{selectedContract.nomenclatura || '-'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Tipo de Objeto:</strong>
                        <Badge 
                          color={getTypeColor(selectedContract.objetocontractual)}
                          style={{
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: '600',
                            padding: '4px 8px',
                            borderRadius: '12px'
                          }}
                        >
                          {selectedContract.objetocontractual || 'N/A'}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-start py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Tipo de Proceso:</strong>
                        <span className="fw-medium text-dark text-end" style={{ maxWidth: '60%', fontSize: isMobile ? '12px' : '14px' }}>
                          {selectedContract.tipoprocesoseleccion || '-'}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Items:</strong>
                        <Badge 
                          color="success"
                          style={{
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: '600',
                            padding: '4px 8px',
                            borderRadius: '12px'
                          }}
                        >
                          {selectedContract.total_items || 0}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Revisado:</strong>
                        <Badge 
                          color={isProcessReviewed(selectedContract) ? 'success' : 'secondary'}
                          style={{
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: '600',
                            padding: '4px 8px',
                            borderRadius: '12px'
                          }}
                        >
                          {isProcessReviewed(selectedContract) ? '✓ Sí' : '○ No'}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Estado:</strong>
                        <Badge 
                          color={getEstadoColor(selectedContract.estado_convocatoria)}
                          style={{
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: '600',
                            padding: '4px 8px',
                            borderRadius: '12px'
                          }}
                        >
                          {selectedContract.estado_convocatoria || '-'}
                        </Badge>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Fechas Importantes */}
              <Col xs={12} md={6}>
                <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <CardBody className={`${isMobile ? 'p-3' : 'p-4'}`}>
                    <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: '#ffc107', fontSize: isMobile ? '1rem' : '1.1rem' }}>
                      <span className="me-2" style={{ fontSize: '1.2rem' }}>📅</span>
                      Fechas Importantes
                    </h6>
                    <div className="space-y-2">
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Fecha de Publicación PAC:</strong>
                        <span className="fw-medium text-dark" style={{ fontSize: isMobile ? '12px' : '14px' }}>{formatDate(selectedContract.fecha_publicacion)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Fecha de Convocatoria:</strong>
                        <span className="fw-medium text-dark" style={{ fontSize: isMobile ? '12px' : '14px' }}>{formatDate(selectedContract.fechaConvocatoria)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Inicio Participantes:</strong>
                        <span className="fw-bold text-success" style={{ fontSize: isMobile ? '12px' : '14px' }}>{formatDate(selectedContract.fecInicioParticipantes)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                      <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Fin Participantes:</strong>
                        <span className="fw-bold text-danger" style={{ fontSize: isMobile ? '12px' : '14px' }}>{formatDate(selectedContract.fecFinParticipantes)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <strong className="text-muted" style={{ fontSize: isMobile ? '12px' : '14px' }}>Mes Previsto:</strong>
                        <span className="fw-medium text-dark" style={{ fontSize: isMobile ? '12px' : '14px' }}>{selectedContract.mes_previsto || '-'}</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Descripción del Proceso */}
              <Col xs={12} md={6}>
                <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <CardBody className={`${isMobile ? 'p-3' : 'p-4'}`}>
                    <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: '#6f42c1', fontSize: isMobile ? '1rem' : '1.1rem' }}>
                      <span className="me-2" style={{ fontSize: '1.2rem' }}>📝</span>
                      Descripción de la convocatoria
                    </h6>
                    <div className="bg-white p-3 rounded-3 border" style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto',
                      lineHeight: '1.6',
                      fontSize: isMobile ? '13px' : '14px'
                    }}>
                      {selectedContract.descripcion_proceso || 'No hay descripción disponible'}
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Items del Proceso */}
              <Col xs={12}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <CardBody className={`${isMobile ? 'p-3' : 'p-4'}`}>
                    <h6 className="fw-bold mb-3 d-flex align-items-center justify-content-between" style={{ color: '#dc3545', fontSize: isMobile ? '1rem' : '1.1rem' }}>
                      <span className="d-flex align-items-center">
                        <span className="me-2" style={{ fontSize: '1.2rem' }}>📦</span>
                        Items
                      </span>
                      <Badge 
                        color="danger"
                        style={{
                          fontSize: isMobile ? '10px' : '12px',
                          fontWeight: '600',
                          padding: '6px 12px',
                          borderRadius: '15px'
                        }}
                      >
                        {selectedContract.items?.length || 0} items
                      </Badge>
                    </h6>
                    
                    {selectedContract.items && selectedContract.items.length > 0 ? (
                      <div style={{ maxHeight: isMobile ? '300px' : '400px', overflowY: 'auto' }}>
                        {isMobile ? (
                          // Vista de tarjetas para móvil
                          <div className="d-grid gap-2">
                            {selectedContract.items.map((item, index) => (
                              <Card key={index} className="border-light" style={{ borderRadius: '8px' }}>
                                <CardBody className="p-2">
                                  <div className="d-flex justify-content-between align-items-start mb-1">
                                    <Badge color="primary" style={{ fontSize: '10px', padding: '2px 6px' }}>
                                      Item {item.n_item}
                                    </Badge>
                                    <small className="fw-bold text-success">
                                      {item.cantidad} {item.unidad_medida}
                                    </small>
                                  </div>
                                  <p className="mb-0" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                                    {item.descripcion_item}
                                  </p>
                                </CardBody>
                              </Card>
                            ))}
                          </div>
                        ) : (
                          // Vista de tabla para desktop/tablet
                          <Table responsive size="sm" className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ fontSize: '12px', fontWeight: '700', width: '80px' }}>N° Item</th>
                                <th style={{ fontSize: '12px', fontWeight: '700' }}>Descripción</th>
                                <th style={{ fontSize: '12px', fontWeight: '700', width: '100px' }}>Cantidad</th>
                                <th style={{ fontSize: '12px', fontWeight: '700', width: '120px' }}>Unidad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedContract.items.map((item, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                                  <td className="text-center" style={{ fontSize: '12px', fontWeight: '600' }}>
                                    <Badge color="primary" style={{ fontSize: '10px' }}>
                                      {item.n_item}
                                    </Badge>
                                  </td>
                                  <td style={{ fontSize: '12px', lineHeight: '1.4' }}>
                                    {item.descripcion_item}
                                  </td>
                                  <td className="text-center fw-bold text-success" style={{ fontSize: '12px' }}>
                                    {item.cantidad}
                                  </td>
                                  <td className="text-center" style={{ fontSize: '12px' }}>
                                    {item.unidad_medida}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="mb-2" style={{ fontSize: '2rem', opacity: 0.3 }}>📦</div>
                        <p className="text-muted mb-0" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                          No hay items disponibles para este proceso
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </ModalBody>
        
        <ModalFooter 
          className="border-0 pt-0"
          style={{
            backgroundColor: '#f8f9fa',
            borderRadius: isMobile ? '0' : '0 0 20px 20px'
          }}
        >
          <div className={`d-flex ${isMobile ? 'flex-column w-100 gap-2' : 'flex-row gap-3'} justify-content-between align-items-center`}>
            <div className={`${isMobile ? 'w-100' : ''}`}>
              {selectedContract && (
                <ReviewCheckbox 
                  process={selectedContract} 
                  user={user}
                  className={`${isMobile ? 'justify-content-center' : 'justify-content-start'}`}
                />
              )}
            </div>
            
            <div className={`d-flex ${isMobile ? 'w-100' : ''} gap-2`}>
              {selectedContract?.url_doc && (
                <Button
                  color="warning"
                  size={isMobile ? 'sm' : 'md'}
                  onClick={() => window.open(selectedContract.url_doc, '_blank')}
                  className={`${isMobile ? 'flex-fill' : ''}`}
                  style={{
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: isMobile ? '12px' : '14px',
                    padding: isMobile ? '8px 12px' : '10px 16px'
                  }}
                >
                  📄 Ver Bases
                </Button>
              )}
              
              <Button
                color="secondary"
                size={isMobile ? 'sm' : 'md'}
                onClick={closeModal}
                className={`${isMobile ? 'flex-fill' : ''}`}
                style={{
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '8px 12px' : '10px 16px'
                }}
              >
                ✖️ Cerrar
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default PACGeneral;